<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import type { Trip, Expense, Poll } from "../../lib/types";
  import { pollsApi, tripsApi } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import { formatCurrency } from "../../lib/utils/format";
  import ExpenseList from "../../components/data-display/ExpenseList.svelte";
  import BudgetProgress from "../../components/data-display/BudgetProgress.svelte";
  import FormField from "../../components/input/FormField.svelte";
  import Layout from "../../components/layout/Layout.svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Modal from "../../components/overlay/Modal.svelte";
  import CompanionModal from "../../components/social/CompanionModal.svelte";
  import PollCard from "../../components/social/PollCard.svelte";
  import Button from "../../components/input/Button.svelte";
  import TextInput from "../../components/input/TextInput.svelte";

  let { trip, expenses = [], user = null }: {
    trip: Trip & { isPublic?: boolean };
    expenses?: Expense[];
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  let isPublic = $state(false);
  let publicStateHydrated = false;
  let togglingPublic = $state(false);
  let showCompanionModal = $state(false);
  let polls = $state<Poll[]>([]);
  let pollsLoading = $state(false);
  let votingOptionId: string | null = $state(null);
  let closingPollId: string | null = $state(null);
  let pollsHydratedTripId: string | null = $state(null);
  let showCreatePollModal = $state(false);
  let creatingPoll = $state(false);
  let pollTitle = $state("");
  let pollDescription = $state("");
  let pollSelectionType = $state<"single_choice" | "multiple_choice">("single_choice");
  let pollOptionInputs = $state(["", ""]);

  const isOwner = $derived(user?.id === trip.userId);

  $effect(() => {
    if (publicStateHydrated) return;
    isPublic = trip.isPublic || false;
    publicStateHydrated = true;
  });

  $effect(() => {
    if (pollsHydratedTripId === trip.id) return;
    pollsHydratedTripId = trip.id;
    loadPolls();
  });

  async function togglePublic() {
    if (!isOwner) return;
    
    togglingPublic = true;
    try {
      const newValue = !isPublic;
      await tripsApi.togglePublic(trip.id, newValue);
      isPublic = newValue;
      addNotification("success", isPublic ? "여행이 공개되었습니다." : "여행이 비공개되었습니다.");
    } catch (error: any) {
      addNotification("error", "설정 변경에 실패했습니다.");
    } finally {
      togglingPublic = false;
    }
  }

  function handleCompanionRefresh() {
    router.reload();
  }

  async function loadPolls() {
    pollsLoading = true;
    try {
      const response = await pollsApi.list(trip.id);
      polls = response.data;
    } catch (error: any) {
      addNotification("error", "투표 목록을 불러오지 못했습니다.");
    } finally {
      pollsLoading = false;
    }
  }

  async function handleVote(optionId: string) {
    votingOptionId = optionId;
    try {
      const response = await pollsApi.vote(optionId);
      polls = polls.map((poll) => (poll.id === response.data.id ? response.data : poll));
      addNotification("success", "투표가 반영되었습니다.");
    } catch (error: any) {
      addNotification("error", error?.response?.data?.error?.message || "투표 처리에 실패했습니다.");
    } finally {
      votingOptionId = null;
    }
  }

  async function handleClosePoll(pollId: string) {
    if (!confirm("이 투표를 마감하시겠습니까?")) return;

    closingPollId = pollId;
    try {
      const response = await pollsApi.close(trip.id, pollId);
      polls = polls.map((poll) => (poll.id === response.data.id ? response.data : poll));
      addNotification("success", "투표가 마감되었습니다.");
    } catch (error: any) {
      addNotification("error", error?.response?.data?.error?.message || "투표 마감에 실패했습니다.");
    } finally {
      closingPollId = null;
    }
  }

  function resetPollForm() {
    pollTitle = "";
    pollDescription = "";
    pollSelectionType = "single_choice";
    pollOptionInputs = ["", ""];
  }

  function openCreatePollModal() {
    resetPollForm();
    showCreatePollModal = true;
  }

  function closeCreatePollModal() {
    showCreatePollModal = false;
  }

  function updateOption(index: number, value: string) {
    pollOptionInputs[index] = value;
    pollOptionInputs = [...pollOptionInputs];
  }

  function addPollOptionField() {
    pollOptionInputs = [...pollOptionInputs, ""];
  }

  function removePollOptionField(index: number) {
    if (pollOptionInputs.length <= 2) return;
    pollOptionInputs = pollOptionInputs.filter((_, currentIndex) => currentIndex !== index);
  }

  async function handleCreatePoll() {
    const trimmedTitle = pollTitle.trim();
    const options = pollOptionInputs
      .map((option, index) => ({ title: option.trim(), position: index }))
      .filter((option) => option.title.length > 0);

    if (!trimmedTitle) {
      addNotification("error", "투표 제목을 입력해주세요.");
      return;
    }

    if (options.length < 2) {
      addNotification("error", "선택지는 최소 2개 이상 필요합니다.");
      return;
    }

    creatingPoll = true;
    try {
      const response = await pollsApi.create(trip.id, {
        title: trimmedTitle,
        description: pollDescription.trim() || undefined,
        selectionType: pollSelectionType,
        options,
      });
      polls = [response.data, ...polls];
      addNotification("success", "새 투표가 생성되었습니다.");
      showCreatePollModal = false;
      resetPollForm();
    } catch (error: any) {
      addNotification("error", error?.response?.data?.error?.message || "투표 생성에 실패했습니다.");
    } finally {
      creatingPoll = false;
    }
  }
</script>

<Layout {user}>
  <div class="min-h-screen" style="background: var(--bg);">
    <!-- Trip Sub Navigation -->
    <TripSubNav {trip} currentPage="overview" />

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="grid gap-6 lg:grid-cols-3">
        <!-- Budget Card -->
        <div class="lg:col-span-2">
          <div class="card">
            <h2 class="text-lg font-semibold mb-4" style="color: var(--text-primary);">예산 현황</h2>
            <BudgetProgress
              budget={trip.budgetCents}
              spent={trip.spentCents}
              currency={trip.budgetCurrency}
            />

            <div class="grid grid-cols-3 gap-4 mt-6">
              <div class="text-center p-4 rounded-lg" style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);">
                <p class="text-sm" style="color: var(--text-muted);">총 예산</p>
                <p class="text-xl font-semibold" style="color: var(--text-primary);">
                  ₩{formatCurrency(trip.budgetCents)}
                </p>
              </div>
              <div class="text-center p-4 rounded-lg" style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);">
                <p class="text-sm" style="color: var(--text-muted);">사용 금액</p>
                <p class="text-xl font-semibold" style="color: #f87171;">
                  ₩{formatCurrency(trip.spentCents)}
                </p>
              </div>
              <div class="text-center p-4 rounded-lg" style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);">
                <p class="text-sm" style="color: var(--text-muted);">남은 금액</p>
                <p class="text-xl font-semibold" style="color: {trip.budgetCents - trip.spentCents >= 0 ? '#34d399' : '#f87171'};">
                  ₩{formatCurrency(Math.abs(trip.budgetCents - trip.spentCents))}
                </p>
              </div>
            </div>

            <!-- Daily Average -->
            {#if trip.durationDays > 0}
              <div class="mt-6 pt-6" style="border-top: 1px solid rgba(255,255,255,0.07);">
                <div class="flex justify-between items-center">
                  <div>
                    <p class="text-sm" style="color: var(--text-muted);">일일 평균 지출</p>
                    <p class="text-lg font-semibold" style="color: var(--text-primary);">
                      ₩{formatCurrency(Math.round(trip.spentCents / trip.durationDays))}
                    </p>
                  </div>
                  <div class="text-right">
                    <p class="text-sm" style="color: var(--text-muted);">일일 예산</p>
                    <p class="text-lg font-semibold" style="color: var(--accent);">
                      ₩{formatCurrency(Math.round(trip.budgetCents / trip.durationDays))}
                    </p>
                  </div>
                </div>
              </div>
            {/if}
          </div>
        </div>

        <!-- Participants & Settings Card -->
        <div class="space-y-6">
          <!-- Participants -->
          <div class="card">
            <h2 class="text-lg font-semibold mb-4" style="color: var(--text-primary);">참여자</h2>
            {#if trip.participants && trip.participants.length > 0}
              <div class="space-y-3">
                {#each trip.participants as participant}
                  <Link href={`/users/${participant.id}`} class="flex items-center p-2 rounded-lg transition-colors" style="color: inherit;" onmouseenter={(e) => (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.05)'} onmouseleave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                    <div
                      class="w-10 h-10 rounded-full flex items-center justify-center font-medium"
                      style="background: rgba(32,178,170,0.15); color: var(--accent);"
                    >
                      {participant.name.charAt(0)}
                    </div>
                    <div class="ml-3 flex-1">
                      <p class="font-medium" style="color: var(--text-primary);">{participant.name}</p>
                      <p class="text-sm" style="color: var(--text-muted);">
                        {participant.role === "owner" ? "방장" : "멤버"}
                      </p>
                    </div>
                    {#if participant.role === "owner"}
                      <svg class="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    {/if}
                  </Link>
                {/each}
              </div>
            {:else}
              <p class="text-muted text-center py-4">참여자가 없습니다.</p>
            {/if}

            <Button variant="secondary" fullWidth size="sm" class="mt-4" onclick={() => showCompanionModal = true}>
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              참여자 초대하기
            </Button>
          </div>

          <!-- Public Setting (Only for completed trips and owner) -->
          {#if isOwner && trip.status === "completed"}
            <div class="card">
              <h2 class="text-lg font-semibold mb-4" style="color: var(--text-primary);">공개 설정</h2>
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium" style="color: var(--text-primary);">여행 공개하기</p>
                  <p class="text-sm" style="color: var(--text-muted);">다른 사용자가 내 프로필에서 볼 수 있습니다</p>
                </div>
                <button
                  onclick={togglePublic}
                  disabled={togglingPublic}
                  aria-label={isPublic ? "여행 공개 끄기" : "여행 공개 켜기"}
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50"
                  style="background: {isPublic ? 'var(--accent)' : 'rgba(255,255,255,0.15)'};"
                  role="switch"
                  aria-checked={isPublic}
                >
                  <span
                    class="pointer-events-none inline-block h-5 w-5 transform rounded-full shadow ring-0 transition duration-200 ease-in-out"
                    style="background: white; transform: translateX({isPublic ? '20px' : '0px'});"
                  ></span>
                </button>
              </div>
              {#if isPublic}
                <div class="mt-4 p-3 rounded-lg text-sm flex items-center gap-2" style="background: rgba(16,185,129,0.12); color: #34d399;">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  이 여행은 팔로워들에게 공개됩니다
                </div>
              {/if}
            </div>
          {/if}

          <!-- Quick Actions -->
          <div class="card">
            <h2 class="text-lg font-semibold mb-4" style="color: var(--text-primary);">빠른 실행</h2>
            <div class="grid grid-cols-2 gap-3">
              <Link
                href={`/trips/${trip.id}/map`}
                class="quick-action-btn flex flex-col items-center p-4 rounded-xl transition-all"
                style="background: rgba(32,178,170,0.1); border: 1px solid rgba(32,178,170,0.2);"
              >
                <svg class="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--accent);">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <span class="text-sm font-medium" style="color: var(--accent);">Map Planner</span>
              </Link>
              <Link
                href={`/trips/${trip.id}/finance`}
                class="quick-action-btn flex flex-col items-center p-4 rounded-xl transition-all"
                style="background: rgba(16,185,129,0.1); border: 1px solid rgba(16,185,129,0.2);"
              >
                <svg class="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: #34d399;">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span class="text-sm font-medium" style="color: #34d399;">Finance Hub</span>
              </Link>
              <Link
                href={`/trips/${trip.id}/story`}
                class="quick-action-btn flex flex-col items-center p-4 rounded-xl transition-all"
                style="background: rgba(139,92,246,0.1); border: 1px solid rgba(139,92,246,0.2);"
              >
                <svg class="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: #a78bfa;">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
                <span class="text-sm font-medium" style="color: #a78bfa;">Story Studio</span>
              </Link>
              <Link
                href={`/trips/${trip.id}/settlement`}
                class="quick-action-btn flex flex-col items-center p-4 rounded-xl transition-all"
                style="background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.2);"
              >
                <svg class="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: #fbbf24;">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span class="text-sm font-medium" style="color: #fbbf24;">정산하기</span>
              </Link>
            </div>
          </div>

          <div class="card">
            <div class="flex items-center justify-between mb-4 gap-3">
              <div>
                <h2 class="text-lg font-semibold" style="color: var(--text-primary);">그룹 의사결정</h2>
                <p class="text-sm" style="color: var(--text-muted);">동행자와 빠르게 투표를 진행하고 결정을 모아보세요.</p>
              </div>
              {#if isOwner}
                <Button variant="secondary" size="sm" onclick={openCreatePollModal}>투표 만들기</Button>
              {/if}
            </div>

            {#if pollsLoading}
              <div class="text-sm py-8 text-center" style="color: var(--text-muted);">투표를 불러오는 중...</div>
            {:else if polls.length > 0}
              <div class="space-y-4">
                {#each polls as poll (poll.id)}
                  <PollCard
                    {poll}
                    loading={votingOptionId !== null || closingPollId === poll.id}
                    onVote={handleVote}
                    onClose={isOwner && poll.status === 'open' ? () => handleClosePoll(poll.id) : undefined}
                  />
                {/each}
              </div>
            {:else}
              <div class="text-center py-8 px-4 rounded-xl" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);">
                <p class="font-medium mb-2" style="color: var(--text-primary);">아직 생성된 투표가 없습니다.</p>
                <p class="text-sm" style="color: var(--text-muted);">맛집, 이동 수단, 일정 우선순위 같은 결정을 투표로 정리할 수 있습니다.</p>
              </div>
            {/if}
          </div>
        </div>

        <!-- Recent Expenses -->
        <div class="lg:col-span-3">
          <div class="card">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-semibold" style="color: var(--text-primary);">최근 지출</h2>
              <Link
                href={`/trips/${trip.id}/expenses`}
                class="text-sm font-medium transition-colors"
                style="color: var(--accent);"
              >
                전체 보기 →
              </Link>
            </div>

            {#if expenses.length > 0}
              <ExpenseList {expenses} compact />
            {:else}
              <div class="text-center py-12" style="color: var(--text-muted);">
                <svg class="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted); opacity: 0.4;">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p class="mt-4">아직 기록된 지출이 없습니다.</p>
                <Link
                  href={`/trips/${trip.id}/expenses`}
                  class="inline-block mt-4 btn btn-primary"
                >
                  지출 추가하기
                </Link>
              </div>
            {/if}
          </div>
        </div>
      </div>
    </main>
  </div>

  <!-- Companion Modal -->
  <!-- Note: quick-action-btn hover handled via :global in style block below -->
  <CompanionModal
    show={showCompanionModal}
    tripId={trip.id}
    companions={trip.participants?.map(p => ({ id: p.id, user: { id: p.id, name: p.name, nickname: '', avatar_url: p.avatarUrl || null }, role: p.role })) || []}
    onClose={() => showCompanionModal = false}
    onRefresh={handleCompanionRefresh}
  />

  <Modal show={showCreatePollModal} title="새 투표 만들기" size="lg" onClose={closeCreatePollModal}>
    <div class="space-y-4">
      <FormField label="투표 제목" required={true}>
        <TextInput value={pollTitle} placeholder="예: 첫날 저녁 메뉴 정하기" oninput={(e) => pollTitle = (e.currentTarget as HTMLInputElement).value} />
      </FormField>

      <FormField label="설명" hint="선택 이유나 상황을 간단히 적어두면 좋습니다.">
        <TextInput value={pollDescription} placeholder="예: 도착 후 바로 갈 수 있는 곳 위주로 정해요" oninput={(e) => pollDescription = (e.currentTarget as HTMLInputElement).value} />
      </FormField>

      <FormField label="선택 방식">
        <div class="grid grid-cols-2 gap-2">
          <button
            type="button"
            class="rounded-xl px-3 py-2 text-sm border transition-colors"
            style="background: {pollSelectionType === 'single_choice' ? 'color-mix(in srgb, var(--accent-dim) 78%, var(--surface-hover))' : 'color-mix(in srgb, var(--surface-hover) 92%, transparent)'}; border-color: {pollSelectionType === 'single_choice' ? 'color-mix(in srgb, var(--accent) 40%, var(--border))' : 'var(--border)'}; color: var(--text-primary);"
            onclick={() => pollSelectionType = 'single_choice'}
          >
            단일 선택
          </button>
          <button
            type="button"
            class="rounded-xl px-3 py-2 text-sm border transition-colors"
            style="background: {pollSelectionType === 'multiple_choice' ? 'color-mix(in srgb, var(--accent-dim) 78%, var(--surface-hover))' : 'color-mix(in srgb, var(--surface-hover) 92%, transparent)'}; border-color: {pollSelectionType === 'multiple_choice' ? 'color-mix(in srgb, var(--accent) 40%, var(--border))' : 'var(--border)'}; color: var(--text-primary);"
            onclick={() => pollSelectionType = 'multiple_choice'}
          >
            복수 선택
          </button>
        </div>
      </FormField>

      <FormField label="선택지" required={true} hint="최소 2개의 선택지를 입력해주세요.">
        <div class="space-y-2">
          {#each pollOptionInputs as option, index (index)}
            <div class="flex items-center gap-2">
              <TextInput value={option} placeholder={`선택지 ${index + 1}`} oninput={(e) => updateOption(index, (e.currentTarget as HTMLInputElement).value)} />
              {#if pollOptionInputs.length > 2}
                <button
                  type="button"
                  class="px-3 py-2 rounded-xl text-sm"
                  style="background: rgba(239,68,68,0.12); color: #f87171;"
                  onclick={() => removePollOptionField(index)}
                >
                  삭제
                </button>
              {/if}
            </div>
          {/each}
          <button
            type="button"
            class="text-sm font-medium"
            style="color: var(--accent);"
            onclick={addPollOptionField}
          >
            + 선택지 추가
          </button>
        </div>
      </FormField>

      <div class="flex justify-end gap-2 pt-2">
        <Button variant="ghost" onclick={closeCreatePollModal}>취소</Button>
        <Button variant="primary" loading={creatingPoll} onclick={handleCreatePoll}>생성하기</Button>
      </div>
    </div>
  </Modal>
</Layout>

<style>
  :global(.quick-action-btn:hover) {
    filter: brightness(1.2);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.3);
  }
</style>
